import { Link, useNavigate } from "react-router-dom";
import { Shield, Ban, Database, Eye, FileText } from "lucide-react";
import SimpleNavigation from "./SimpleNavigation";

const shortVersion = [
  {
    icon: <Ban size={20} strokeWidth={1.5} />,
    title: "No Ads",
    description: "We don't show ads, so we don't track you for advertising.",
  },
  {
    icon: <Database size={20} strokeWidth={1.5} />,
    title: "No Selling Data",
    description: "Your reading data is yours. We never sell it to anyone.",
  },
  {
    icon: <Eye size={20} strokeWidth={1.5} />,
    title: "Minimal Collection",
    description:
      "We only collect what's necessary to run the app and this website.",
  },
  {
    icon: <Shield size={20} strokeWidth={1.5} />,
    title: "You're in Control",
    description: "Request export or deletion of your data at any time.",
  },
];

const rights = [
  {
    title: "Access Your Data",
    description:
      "You can request a copy of all personal data we hold about you at any time.",
  },
  {
    title: "Delete Your Data",
    description:
      "You can request deletion of your data by contacting us. We'll process it within 30 days.",
  },
  {
    title: "Control Public Information",
    description:
      "Once the app launches, you'll control what information is visible to other users.",
  },
  {
    title: "Opt Out of Communications",
    description:
      "Unsubscribe from non-essential emails at any time via the link in any email we send.",
  },
  {
    title: "Lodge a Complaint",
    description:
      "As an EU resident, you have the right to lodge a complaint with your national data protection authority (in Belgium: the GBA / Autorité de protection des données).",
  },
];

const securityMeasures = [
  "Encrypted data transmission (HTTPS/TLS)",
  "Secure cloud infrastructure via Google Firebase",
  "Limited access to personal data — only essential team members",
  "No plain-text password storage",
  "Regular review of third-party service security practices",
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const goToContact = () => {
    navigate("/contact");
    setTimeout(() => {
      document
        .getElementById("contact")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <>
      <SimpleNavigation />

      <main className="bg-primary min-h-screen font-serif">
        {/* Hero */}
        <section className="text-center px-6 pt-16 pb-12">
          <div className="w-20 h-20 rounded-full bg-accent-light inline-flex items-center justify-center mb-7 text-accent">
            <Shield size={36} strokeWidth={1.5} />
          </div>
          <h1 className="text-5xl font-normal tracking-tight text-[#1a1a1a] mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-400">Last updated: March 5, 2026</p>
        </section>

        {/* Intro callout */}
        <div className="max-w-3xl mx-auto px-8 pb-12">
          <div className="border border-accent-light rounded-2xl p-7 text-sm text-[#444] leading-7">
            At Bookshelf, your privacy is not just a feature—it's a fundamental
            right. This policy explains how we collect, use, and protect your
            personal information across both this website and the Bookshelf
            mobile app. We've written it in plain English because transparency
            matters.
          </div>
        </div>

        {/* The Short Version */}
        <section className="max-w-3xl mx-auto px-8 pb-16">
          <h2 className="text-2xl font-normal text-[#1a1a1a] mb-7">
            The Short Version
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {shortVersion.map((item) => (
              <div
                key={item.title}
                className="bg-white border border-accent-light rounded-2xl p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-experience-bg flex items-center justify-center mb-4 text-accent">
                  {item.icon}
                </div>
                <h3 className="text-base font-semibold text-[#1a1a1a] mb-1.5">
                  {item.title}
                </h3>
                <p className="text-sm text-[#666] leading-6">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Full policy content */}
        <section className="bg-primary">
          <div className="max-w-3xl mx-auto px-8 py-16 space-y-14">
            {/* Who We Are */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Who We Are
              </h2>
              <p className="text-sm text-[#444] leading-7">
                Bookshelf is a mobile reading companion app currently in beta.
                This landing page is operated to collect beta tester sign-ups
                and provide information about the app. The data controller for
                personal information collected via this website is the Bookshelf
                team, reachable at{" "}
                <a
                  href="mailto:info@groweasy.be"
                  className="text-accent hover:text-primary-dark transition"
                >
                  info@groweasy.be
                </a>
                .
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Information We Collect
              </h2>

              <h3 className="text-base font-semibold text-[#1a1a1a] mb-3">
                On This Website (Beta Sign-Up)
              </h3>
              <p className="text-sm text-[#444] leading-7 mb-3">
                When you register as a beta tester, we collect:
              </p>
              <ul className="list-disc list-inside text-sm text-[#444] leading-8 ml-4 mb-6">
                <li>First and last name</li>
                <li>Email address</li>
                <li>Phone operating system (iOS or Android)</li>
                <li>Reading preferences and favorite genres (optional)</li>
                <li>Any thoughts or feedback you choose to share (optional)</li>
              </ul>

              <h3 className="text-base font-semibold text-[#1a1a1a] mb-3">
                Contact Form
              </h3>
              <p className="text-sm text-[#444] leading-7 mb-3">
                When you use our contact form, we collect:
              </p>
              <ul className="list-disc list-inside text-sm text-[#444] leading-8 ml-4 mb-6">
                <li>Your name and email address</li>
                <li>The subject and content of your message</li>
              </ul>

              <h3 className="text-base font-semibold text-[#1a1a1a] mb-3">
                In the Bookshelf App (Beta)
              </h3>
              <p className="text-sm text-[#444] leading-7 mb-3">
                Once the app is available, we will collect:
              </p>
              <ul className="list-disc list-inside text-sm text-[#444] leading-8 ml-4 mb-6">
                <li>Email address and display name (for your account)</li>
                <li>Books you add to your shelves and reading progress</li>
                <li>Reviews and ratings you write</li>
                <li>Custom shelves and tags you create</li>
                <li>
                  Books and reading history imported from Goodreads (if you
                  choose to use this feature)
                </li>
              </ul>

              <h3 className="text-base font-semibold text-[#1a1a1a] mb-3">
                Usage & Analytics
              </h3>
              <p className="text-sm text-[#444] leading-7 mb-3">
                This website uses Vercel Analytics to collect anonymised,
                aggregated usage data such as page views and general traffic
                patterns. This data cannot be used to identify you individually
                and no cookies are used for tracking purposes.
              </p>
              <p className="text-sm text-[#555] leading-7 italic">
                Note: We use privacy-respecting analytics that do not track you
                across sites or apps.
              </p>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                How We Use Your Information
              </h2>
              <p className="text-sm text-[#444] leading-7 mb-4">
                We use your information to:
              </p>
              <ul className="text-sm text-[#444] leading-8 space-y-2 ml-1">
                <li>
                  <span className="font-semibold">Send beta access:</span>{" "}
                  Notify you when the app is ready and grant early access.
                </li>
                <li>
                  <span className="font-semibold">Respond to messages:</span>{" "}
                  Reply to your contact form submissions and support requests.
                </li>
                <li>
                  <span className="font-semibold">Improve the app:</span> Use
                  aggregated, anonymised feedback to understand what features
                  matter most.
                </li>
                <li>
                  <span className="font-semibold">Communicate with you:</span>{" "}
                  Send important updates about the beta and the app launch.
                </li>
              </ul>
              <p className="text-sm text-[#444] leading-7 mt-5 font-semibold">
                We will never use your information to show you targeted ads or
                sell it to third parties.
              </p>
            </div>

            {/* Legal Basis (GDPR) */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Legal Basis for Processing (GDPR)
              </h2>
              <p className="text-sm text-[#444] leading-7 mb-4">
                As we operate from Belgium, we process your data under the
                following legal bases as defined by the General Data Protection
                Regulation (GDPR):
              </p>
              <ul className="text-sm text-[#444] leading-8 space-y-2 ml-1">
                <li>
                  <span className="font-semibold">Consent:</span> You
                  voluntarily submit your information via the beta sign-up or
                  contact form.
                </li>
                <li>
                  <span className="font-semibold">Legitimate interests:</span>{" "}
                  Responding to your messages and improving our product.
                </li>
              </ul>
              <p className="text-sm text-[#444] leading-7 mt-4">
                You may withdraw your consent at any time by contacting us at{" "}
                <a
                  href="mailto:info@groweasy.be"
                  className="text-accent hover:text-primary-dark transition"
                >
                  info@groweasy.be
                </a>
                .
              </p>
            </div>

            {/* Data Sharing */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Data Sharing and Disclosure
              </h2>
              <p className="text-sm text-[#444] leading-7 font-semibold mb-4">
                We do not sell your personal information. Period.
              </p>
              <p className="text-sm text-[#444] leading-7 mb-4">
                We share your information only with the following trusted
                service providers, and only to the extent necessary to operate
                this website and the app:
              </p>
              <ul className="text-sm text-[#444] leading-8 space-y-2 ml-1 mb-4">
                <li>
                  <span className="font-semibold">
                    Google Firebase (Google LLC):
                  </span>{" "}
                  We use Firebase for database storage (Firestore) and backend
                  functions. Data may be stored on Google servers within the EU
                  or US under Google's standard contractual clauses.
                </li>
                <li>
                  <span className="font-semibold">Resend:</span> We use Resend
                  to send transactional emails (e.g., beta confirmation, contact
                  form replies). Your email address is shared with Resend solely
                  for this purpose.
                </li>
                <li>
                  <span className="font-semibold">Vercel:</span> This website is
                  hosted on Vercel. Vercel may process basic request metadata
                  (e.g., IP addresses) as part of hosting and their analytics
                  product.
                </li>
              </ul>
              <p className="text-sm text-[#444] leading-7">
                We may also disclose information if required by law or to
                protect the rights and safety of our users.
              </p>
            </div>

            {/* Data Retention */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Data Retention
              </h2>
              <p className="text-sm text-[#444] leading-7">
                We retain your beta sign-up information for as long as the beta
                programme is active or until you request deletion. Contact form
                submissions are retained for up to 12 months. You can request
                deletion of your data at any time by emailing us.
              </p>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Data Security
              </h2>
              <div className="bg-experience-bg border border-accent-light rounded-2xl p-7">
                <p className="text-sm text-[#444] leading-7 mb-4">
                  We take security seriously and use industry-standard measures
                  to protect your data:
                </p>
                <ul className="list-disc list-inside text-sm text-[#444] leading-8 ml-2">
                  {securityMeasures.map((measure) => (
                    <li key={measure}>{measure}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-7">
                Your Rights and Choices
              </h2>
              <div className="space-y-4">
                {rights.map((right) => (
                  <div
                    key={right.title}
                    className="bg-experience-bg border border-accent-light rounded-2xl p-6"
                  >
                    <h3 className="text-sm font-semibold text-[#1a1a1a] mb-1.5">
                      {right.title}
                    </h3>
                    <p className="text-sm text-[#666] leading-6">
                      {right.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Children's Privacy
              </h2>
              <p className="text-sm text-[#444] leading-7">
                Bookshelf is not intended for children under 13. We do not
                knowingly collect personal information from children under 13.
                If you believe we have collected information from a child under
                13, please contact us immediately at{" "}
                <a
                  href="mailto:info@groweasy.be"
                  className="text-accent hover:text-primary-dark transition"
                >
                  info@groweasy.be
                </a>{" "}
                and we will delete it promptly.
              </p>
            </div>

            {/* Changes to Policy */}
            <div>
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-5">
                Changes to This Policy
              </h2>
              <p className="text-sm text-[#444] leading-7">
                We may update this privacy policy from time to time as the app
                evolves. We'll notify beta testers of significant changes via
                email. The "last updated" date at the top of this page will
                always reflect the most recent revision. Your continued use of
                the website or app after changes constitutes acceptance of the
                updated policy.
              </p>
            </div>

            {/* Contact CTA */}
            <div className="bg-experience-bg border border-accent-light rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-accent flex-shrink-0">
                <FileText size={22} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-[#1a1a1a] mb-1">
                  Questions About Privacy?
                </h3>
                <p className="text-sm text-[#666] leading-6">
                  If you have questions about this privacy policy or how we
                  handle your data, please don't hesitate to reach out.
                </p>
              </div>
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  goToContact();
                }}
                className="flex-shrink-0 bg-accent hover:bg-primary-dark text-white text-sm font-medium px-6 py-3 rounded-lg transition-colors cursor-pointer"
                to={""}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
